import moment from 'moment';
import {z} from 'zod';

export const zodObjectDocumentUpload = z
    .object({
        id: z.string().nullish(),
        step: z.string().optional(),
        name: z.string().nullish(),
        documentType: z.string(),
        fileId: z.string().nullish(),
        willDropOffOrMailIn: z.union([
            z.boolean(),
            z.string().transform((val: string): boolean => {
                return val === 'Mail';
            }),
        ]),
    })
    // A document only counts as provided once it has been uploaded (has a fileId) or
    // the family chose to mail/drop it off. A card still sitting in "Upload" mode with
    // no file is incomplete and must fail validation.
    .refine(document => document.willDropOffOrMailIn || Boolean(document.fileId), {
        message: 'This document must be uploaded or marked to mail/drop off.',
        path: ['fileId'],
    });

export const phoneValidation = (failMessage: string, optional?: boolean) =>
    z
        .string()
        .nullish()
        .transform(value => {
            return value === '' || value === undefined ? null : value;
        })
        .refine(
            val => {
                if (typeof val === 'string') {
                    return /\+1[0-9]{9}/.test(val) !== null;
                }
                return optional ?? false;
            },
            {
                message: failMessage,
            },
        );

const convertToDate = (date: string) => {
    return new Date(Date.parse(date));
};
export const zodDateRegex = (failMessage?: string) =>
    z
        .string({message: failMessage})
        .regex(/^\d{2}\/\d{2}\/\d{4}$/, 'Invalid date format. Use MM/DD/YYYY.')
        .transform(value => {
            if (value === null || value === '') {
                return null;
            }

            if (moment(convertToDate(value), 'MM/DD/YYYY', true).isValid()) {
                return moment(value).format('MM/DD/YYYY');
            }

            if (moment(convertToDate(value), 'YYYY-MM-DD', true).isValid()) {
                return moment(value).format('MM/DD/YYYY');
            }

            return null;
        });

export const postalCodeUS = (failMessage: string) =>
    z.string().regex(/^\d{5}([ -]\d{4})?/, failMessage);

export const addressWithoutPOBox = (failMessage?: string) =>
    z
        .string({message: failMessage})
        .regex(
            /^(?!.*(?:(.*((p|post)[-.\s]*(o|off|office)[-.\s]*(box|bin)[-.\s]*)|.*((p |post)[-.\s]*(box|bin)[-.\s]*)))).*$/i,
            'Address cannot be a P.O. Box',
        );

export const states = [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'Florida',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
    'Out of Country',
];

export const isState = z.string().refine(str => states.includes(str), {message: 'Invalid state.'});
